import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  So5State,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineL, HeadlineS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { Header } from '../Header';

const CenteredFlexContainer = styled(Vertical).attrs({ gap: 2, center: true })`
  align-self: center;
  justify-content: flex-start;
`;

type Props = {
  errors?: { message: string }[];
  loading: boolean;
  title?: MessageDescriptor;
  subtitle?: MessageDescriptor;
  postAction?: 'lobby' | 'email';
};

export const Form = ({
  loading,
  errors,
  title,
  subtitle,
  postAction,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  return (
    <Vertical gap={3}>
      <Header />
      {!!loading && <LoadingIndicator />}
      <CenteredFlexContainer>
        {errors?.length === 0 && (
          <>
            {!!title && (
              <HeadlineL as="h3">
                <FormattedMessage {...title} />
              </HeadlineL>
            )}
            {!!subtitle && (
              <HeadlineS as="h5">
                <FormattedMessage {...subtitle} />
              </HeadlineS>
            )}
          </>
        )}
        {errors?.map(({ message }) => (
          <HeadlineL as="h3" key={message}>
            {message}
          </HeadlineL>
        ))}
        {postAction === 'lobby' && (
          <Button
            to={generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
              params: {
                fixture: So5State.UPCOMING.toLowerCase(),
                eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
              },
            })}
            color="primary"
            size="medium"
          >
            <FormattedMessage
              id="NoCardEntry.Form.Cta.title"
              defaultMessage="Go back to the lobby"
            />
          </Button>
        )}
        {postAction === 'email' && (
          <HeadlineL as="h3">
            <FormattedMessage
              id="NoCardEntry.Form.email"
              defaultMessage="Please check your emails"
            />
          </HeadlineL>
        )}
      </CenteredFlexContainer>
    </Vertical>
  );
};
